/** Additive routine journal. Immutable events retain concurrent revisions and history. */
import { MAX_ROUTINE_MINUTES, MAX_ROUTINE_SECONDS, DURATION_UNITS } from '../core/routine-duration.js';
import { USER_ORIGIN } from './origins.js';
import { t } from '../../shared/i18n/index.js';
import { dayKey, validDay, normalizeSchedule, addInterval, calendarDates, addDays, nextAllowedDay } from '../core/recurrence.js';
export const ROUTINES_MAP='routines';
const copy=x=>JSON.parse(JSON.stringify(x));
const sequence=e=>Number.isSafeInteger(e?.seq)&&e.seq>0?e.seq:0;
const order=(a,b)=>sequence(a)-sequence(b)||String(a?.id||'').localeCompare(String(b?.id||''));
function validRecord(e) {
  if(!e||typeof e.id!=='string'||!e.id||typeof e.kind!=='string'||!Number.isSafeInteger(e.seq)||e.seq<1||typeof e.at!=='string'||!Number.isFinite(Date.parse(e.at)))return false;
  const steps=()=>Array.isArray(e.steps)&&e.steps.every(s=>s&&typeof s.id==='string'&&typeof s.nodeId==='string'&&typeof s.title==='string'&&typeof s.notes==='string'&&Number.isFinite(s.minutes)&&s.minutes>=0&&s.minutes<=MAX_ROUTINE_MINUTES&&(s.durationUnit===undefined||Object.hasOwn(DURATION_UNITS,s.durationUnit))&&typeof s.target==='string');
  if(e.kind==='definition') {
    try {if(e.schedule)normalizeSchedule(e.schedule);}catch{return false;}
    return typeof e.nodeId==='string'&&typeof e.title==='string'&&typeof e.cue==='string'&&typeof e.scheduleId==='string'&&Array.isArray(e.parents)&&e.parents.every(p=>typeof p==='string')&&steps();
  }
  if(e.kind==='start')return typeof e.nodeId==='string'&&typeof e.title==='string'&&typeof e.occurrenceId==='string'&&typeof e.revisionId==='string'&&typeof e.scheduleId==='string'&&validDay(e.day)&&steps();
  if(e.kind==='result') {
    const v=e.values;
    return typeof e.runId==='string'&&typeof e.stepId==='string'&&v&&typeof v==='object'&&!Array.isArray(v)
      &&(v.seconds===undefined||(Number.isFinite(v.seconds)&&v.seconds>=0&&v.seconds<=MAX_ROUTINE_SECONDS))
      &&(v.done===undefined||typeof v.done==='boolean')&&(v.result===undefined||typeof v.result==='string')
      &&(v.runningAt===undefined||v.runningAt===null||(typeof v.runningAt==='string'&&Number.isFinite(Date.parse(v.runningAt))))
      &&(v.timerOwner===undefined||typeof v.timerOwner==='string');
  }
  if(e.kind==='finish')return typeof e.runId==='string'&&['done','partial','active'].includes(e.state)&&validDay(e.day);
  if(e.kind==='position')return typeof e.nodeId==='string'&&typeof e.noteId==='string'&&typeof e.occurrenceId==='string'&&Number.isFinite(e.order);
  if(['exception','anchor','link'].includes(e.kind))return typeof e.nodeId==='string'&&typeof e.occurrenceId==='string'&&(e.kind==='link'?typeof e.noteId==='string':validDay(e.day));
  return true; // Future event types round-trip, without being interpreted here.
}
export function readRoutineEvents(dm) {return dm ? [...dm.doc.getMap(ROUTINES_MAP).values()].sort(order) : [];}
export function appendRoutineEvent(dm, data) {
  const events=readRoutineEvents(dm);
  const e={...copy(data),id:crypto.randomUUID(),seq:events.reduce((n,e)=>Math.max(n,sequence(e)),0)+1,at:new Date().toISOString()};
  if(!Number.isSafeInteger(e.seq))throw Error(t('routines.error.historyRepair'));
  dm.doc.transact(()=>dm.doc.getMap(ROUTINES_MAP).set(e.id,e),USER_ORIGIN);
  return e;
}
export function importRoutineEvents(dm, events) {
  if (!Array.isArray(events)) throw Error(t('routines.error.invalidHistory'));
  for(const e of events)if(!validRecord(e))throw Error(t('routines.error.invalidRecord'));
  const map=dm.doc.getMap(ROUTINES_MAP);
  // Refuse an ID collision before changing anything; callers retain the import.
  const seen=new Map(map);
  for(const e of events){if(seen.has(e.id)&&JSON.stringify(seen.get(e.id))!==JSON.stringify(e))throw Error(t('routines.error.conflictingIds'));seen.set(e.id,e);}
  dm.doc.transact(()=>{for(const e of events)if(!map.has(e.id))map.set(e.id,copy(e));},USER_ORIGIN);
}
export function routineState(events) {
  const invalid=events.filter(e=>!validRecord(e));
  events=events.filter(validRecord).sort(order);
  const definitions=new Map(), runs=new Map(), links=new Map(), exceptions=new Map(),anchors=new Map(),positions=new Map();
  for(const e of events) {
    if(e.kind==='definition') {if(!definitions.has(e.nodeId))definitions.set(e.nodeId,[]); definitions.get(e.nodeId).push({...e,schedule:normalizeSchedule(e.schedule)});}
    if(e.kind==='start') runs.set(e.id,{...copy(e),runId:e.id,state:'active',results:Object.create(null),finishedAt:null});
  }
  const routines=[];
  for(const [nodeId,versions] of definitions) {
    const superseded=new Set(versions.flatMap(v=>v.parents||[]));
    let heads=versions.filter(v=>!superseded.has(v.id)).sort(order);
    if(!heads.length){heads=versions;invalid.push(...versions);}
    const last=heads.at(-1); routines.push({...last,nodeId,heads,versions,conflict:heads.length>1});
  }
  for(const e of events) {
    const run=runs.get(e.runId);
    if(e.kind==='result' && run) run.results[e.stepId]={...(run.results[e.stepId]||{}),...e.values};
    if(e.kind==='finish' && run) {run.state=e.state;run.finishedAt=e.at;run.finishedDay=e.day;}
    if(e.kind==='position') positions.set(JSON.stringify([e.noteId,e.occurrenceId]),e);
    if(e.kind==='link') links.set(JSON.stringify([e.noteId,e.occurrenceId]),e);
    if(e.kind==='exception') exceptions.set(e.occurrenceId,e);
    if(e.kind==='anchor'){if(!anchors.has(e.occurrenceId))anchors.set(e.occurrenceId,[]);anchors.get(e.occurrenceId).push(e);}
  }
  for(const [id,entries] of anchors){const superseded=new Set(entries.flatMap(e=>e.parents||[]));anchors.set(id,entries.filter(e=>!superseded.has(e.id)));}
  return {routines,runs:[...runs.values()],links:[...links.values()].filter(e=>!e.removed),exceptions,anchors,positions,invalid,linkRecords:[...links.values()]};
}
const stateCache=new WeakMap();
export function getRoutineState(dm) {
  if(!dm)return routineState([]);
  let cache=stateCache.get(dm.doc);
  if(!cache){cache={state:null};stateCache.set(dm.doc,cache);dm.doc.getMap(ROUTINES_MAP).observe(()=>{cache.state=null;});}
  return cache.state ||= routineState(readRoutineEvents(dm));
}
export function saveRoutine(dm, nodeId, raw, parents) {
  if(!nodeId || !dm.hasNode(nodeId))throw Error(t('routines.error.otherMap'));
  const current=routineState(readRoutineEvents(dm)).routines.find(r=>r.nodeId===nodeId);
  if(current?.conflict && !raw.reviewedConflict)throw Error(t('routines.error.reviewVersions'));
  if(current && JSON.stringify([...parents].sort())!==JSON.stringify(current.heads.map(h=>h.id).sort()))throw Error(t('routines.error.changedElsewhere'));
  const steps=(raw.steps||[]).map(s=>{
    if(!s.id||!s.nodeId||(!dm.hasNode(s.nodeId)&&!current?.steps.some(old=>old.id===s.id&&old.nodeId===s.nodeId)))throw Error(t('routines.error.foreignTasks'));
    const minutes=Number(s.minutes||0); if(!Number.isFinite(minutes)||minutes<0||minutes>MAX_ROUTINE_MINUTES)throw Error(t('routines.error.duration'));
    return {id:s.id,nodeId:s.nodeId,title:String(s.title||'Task'),notes:String(s.notes||''),minutes,...(Object.hasOwn(DURATION_UNITS,s.durationUnit)?{durationUnit:s.durationUnit}:{}),target:String(s.target||'')};
  });
  if(new Set(steps.map(s=>s.id)).size!==steps.length)throw Error(t('routines.error.stepIdentity'));
  const schedule=normalizeSchedule(raw.schedule);
  const identity=s=>s?JSON.stringify([s.mode,s.start,s.interval,s.unit,s.zone,s.weekdays,s.monthEnd,...(s.allowedDays?.length<7?[s.allowedDays]:[])]):null;
  const scheduleId=current && identity(schedule)===identity(current.schedule) ? current.scheduleId : crypto.randomUUID();
  return appendRoutineEvent(dm,{kind:'definition',nodeId,parents:parents||[],title:String(raw.title||'Routine'),
    steps,schedule,scheduleId,paused:!!raw.paused,archived:!!raw.archived,cue:String(raw.cue||'')});
}
export function occurrenceId(r, slot) {return JSON.stringify([r.nodeId,r.scheduleId,slot]);}
export function nextOccurrence(r, state, today=dayKey(new Date(),r.schedule?.zone)) {
  if(r.paused||r.archived||r.conflict)return null;
  const s=r.schedule;
  if(!s) {
    const runs=state.runs.filter(run=>run.nodeId===r.nodeId&&run.scheduleId===r.scheduleId);
    const active=runs.find(run=>run.state==='active');
    const pending=state.links.find(link=>{
      if(link.nodeId!==r.nodeId||runs.some(run=>run.occurrenceId===link.occurrenceId&&run.state==='done'))return false;
      try{return JSON.parse(link.occurrenceId)[1]===r.scheduleId;}catch{return false;}
    });
    return {id:active?.occurrenceId||pending?.occurrenceId||null,day:null};
  }
  const completed=new Map();
  for(const run of state.runs.filter(run=>run.nodeId===r.nodeId&&run.scheduleId===r.scheduleId&&run.state==='done')) {
    if(!completed.has(run.occurrenceId))completed.set(run.occurrenceId,[]);
    completed.get(run.occurrenceId).push(run);
  }
  if(s.mode==='completion') {
    let slot='first', day=nextAllowedDay(s.start,s);
    for(let i=0;i<=state.runs.length+state.exceptions.size+1;i++) {
      const id=occurrenceId(r,slot), claims=completed.get(id)||[], exception=state.exceptions.get(id);
      const resolved=state.anchors?.get(id)||[];
      const days=new Set(resolved.length?resolved.map(e=>e.day):claims.map(c=>c.finishedDay));
      if(days.size>1)return {conflict:true,id,day,completionDays:[...days].sort()};
      if(claims.length) {day=nextAllowedDay(addInterval([...days][0],s.interval,s.unit,'clamp'),s);slot=`cycle:${i+1}`;continue;}
      if(exception?.skipped) {day=nextAllowedDay(exception.nextDay,s);slot=`cycle:${i+1}`;continue;}
      if(exception?.day)day=exception.day;
      return !day||(s.until&&day>s.until)?null:{id,day};
    }
    return null;
  }
  // An explicitly moved date or a session already started survives midnight.
  const candidates=[];
  for(const run of state.runs) if(run.nodeId===r.nodeId&&run.scheduleId===r.scheduleId&&run.state==='active'&&!completed.has(run.occurrenceId)) {
    const exception=state.exceptions.get(run.occurrenceId);
    if(!exception?.skipped)candidates.push({id:run.occurrenceId,day:exception?.day||run.scheduledDay});
  }
  for(const exception of state.exceptions.values()) if(exception.nodeId===r.nodeId&&!exception.skipped&&!completed.has(exception.occurrenceId)) {
    try {if(JSON.parse(exception.occurrenceId)[1]===r.scheduleId)candidates.push({id:exception.occurrenceId,day:exception.day});}catch{}
  }
  // Start from today for practice; retain the earliest outstanding maintenance.
  const from=s.missed==='keep'?s.start:today;
  let cursor=from;
  for(let page=0;page<=Math.ceil((state.runs.length+state.exceptions.size)/100)+1;page++) {
    const dates=calendarDates(s,cursor,'9999-12-31',100);
    for(const day of dates) {
      const id=occurrenceId(r,day), exception=state.exceptions.get(id);
      if(!completed.has(id)&&!exception?.skipped&&!exception?.day) {
        candidates.push({id,day,nominalDay:day});
        return candidates.sort((a,b)=>a.day.localeCompare(b.day))[0];
      }
    }
    if(!dates.length)break;cursor=addDays(dates.at(-1),1);if(!cursor)break;
  }
  return candidates.sort((a,b)=>a.day.localeCompare(b.day))[0]||null;
}
export function startRoutine(dm, r, occurrence, snapshot, noteId=null) {
  if(r.paused||r.archived||r.conflict||occurrence?.conflict)throw Error(t('routines.error.resolveBeforeStart'));
  if(!occurrence)throw Error(t('routines.error.scheduleEnded'));
  if(!r.schedule&&!occurrence.id)occurrence={...occurrence,id:occurrenceId(r,`manual:${crypto.randomUUID()}`)};
  let identity;try{identity=JSON.parse(occurrence.id);}catch{throw Error(t('routines.error.invalidOccurrence'));}
  if(!Array.isArray(identity)||identity.length!==3||identity[0]!==r.nodeId||identity[1]!==r.scheduleId||(occurrence.day!==null&&!validDay(occurrence.day)))throw Error(t('routines.error.otherSchedule'));
  const state=routineState(readRoutineEvents(dm));
  if(state.runs.some(run=>run.occurrenceId===occurrence.id&&run.state==='done'))throw Error(t('routines.error.alreadyCompleted'));
  const existing=state.runs.find(run=>run.occurrenceId===occurrence.id&&['active','partial'].includes(run.state));
  if(noteId&&!state.links.some(link=>link.noteId===noteId&&link.occurrenceId===occurrence.id))appendRoutineEvent(dm,{kind:'link',nodeId:r.nodeId,noteId,occurrenceId:occurrence.id,day:occurrence.day,title:r.title,removed:false});
  if(existing) { if(existing.state==='partial')appendRoutineEvent(dm,{kind:'finish',runId:existing.runId,state:'active',day:existing.day});return {...existing,state:'active'}; }
  return appendRoutineEvent(dm,{kind:'start',nodeId:r.nodeId,revisionId:r.id,scheduleId:r.scheduleId,
    occurrenceId:occurrence.id,scheduledDay:occurrence.day,noteId,zone:r.schedule?.zone||Intl.DateTimeFormat().resolvedOptions().timeZone,
    title:r.title,cue:r.cue,steps:copy(snapshot),day:dayKey(new Date(),r.schedule?.zone)});
}
export function routineSummary(runs) {
  const days=new Set(),completed=new Set();let seconds=0;
  for(const run of runs) {
    if(run.state==='done')completed.add(run.occurrenceId);
    let duration=0;for(const result of Object.values(run.results))duration+=Math.max(0,Number(result.seconds)||0);
    seconds+=duration;if(duration||run.state==='done')days.add(run.day);
  }
  return {seconds,done:completed.size,days:days.size,sessions:runs.length};
}
